/*************************************************************************
 *
 * KLEARNOW CONFIDENTIAL
 * __________________
 *
 *  Copyright (c) 2018 - 2020 KLEARNOW Corporation.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of KLEARNOW Corporation and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to KLEARNOW Corporation
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from KLEARNOW Corporation.
 */

import isEqual from 'lodash/isEqual';
import { createSelector } from 'reselect';

// Base selectors
const selectFieldValidationState = (state) => state.FieldValidation;
const selectValidObjectsBl = (state) => state.FieldValidation?.validObjects_bl;
const selectWarningsList = (state) => state.FieldValidation?.warnings || [];

const EMPTY_SLICE = Object.freeze({});
const EMPTY_ARRAY = Object.freeze([]);

export const merchLineValidationKey = (field, merchandiseId) =>
  `Merchandiseqwqr${field}${merchandiseId}`;

export const merchDutyValidationKey = (field, merchandiseDutyId) =>
  `Merchandiseqwqr${field}${merchandiseDutyId}`;

export const invoiceValidationKey = (field, invoiceId) =>
  `Merchandiseqwqr${field}${invoiceId}`;

const INVOICE_GLOBAL_VALIDATION_KEYS = Object.freeze([
  'MerchandiseqwqrinvoiceAmount',
  'MerchandiseqwqrexchangeRate',
]);

function pickMerchandiseLineValidationSlice(validObjectsBl, merchandiseId, merchandiseDutyIds = []) {
  if (!validObjectsBl || !merchandiseId) return EMPTY_SLICE;

  const slice = {};
  const dutySuffixes = new Set((merchandiseDutyIds || []).filter(Boolean));

  if (validObjectsBl[merchandiseId]) {
    slice[merchandiseId] = validObjectsBl[merchandiseId];
  }

  for (const key of Object.keys(validObjectsBl)) {
    if (key.endsWith(merchandiseId)) {
      slice[key] = validObjectsBl[key];
      continue;
    }
    for (const dutyId of dutySuffixes) {
      if (key.endsWith(dutyId)) {
        slice[key] = validObjectsBl[key];
        break;
      }
    }
  }

  return slice;
}

function pickInvoiceValidationSlice(validObjectsBl, invoiceId) {
  if (!validObjectsBl || !invoiceId) return EMPTY_SLICE;

  const slice = {};
  for (const key of Object.keys(validObjectsBl)) {
    if (key.endsWith(invoiceId) || INVOICE_GLOBAL_VALIDATION_KEYS.includes(key)) {
      slice[key] = validObjectsBl[key];
    }
  }
  return slice;
}

const merchLineValidationSelectorCache = new Map();
const invoiceValidationSelectorCache = new Map();
const invoiceWarningsSelectorCache = new Map();

function buildMerchandiseLineValidationSelector(merchandiseId, merchandiseDutyIds) {
  let cachedSlice = EMPTY_SLICE;
  let cachedSource = null;

  return createSelector(
    [selectValidObjectsBl],
    (validObjectsBl) => {
      if (validObjectsBl === cachedSource) return cachedSlice;

      const next = pickMerchandiseLineValidationSlice(
        validObjectsBl,
        merchandiseId,
        merchandiseDutyIds
      );
      cachedSource = validObjectsBl;
      if (isEqual(cachedSlice, next)) return cachedSlice;

      cachedSlice = next;
      return next;
    }
  );
}

function buildInvoiceValidationSelector(invoiceId) {
  let cachedSlice = EMPTY_SLICE;
  let cachedSource = null;

  return createSelector(
    [selectValidObjectsBl],
    (validObjectsBl) => {
      if (validObjectsBl === cachedSource) return cachedSlice;

      const next = pickInvoiceValidationSlice(validObjectsBl, invoiceId);
      cachedSource = validObjectsBl;
      if (isEqual(cachedSlice, next)) return cachedSlice;

      cachedSlice = next;
      return next;
    }
  );
}

function buildInvoiceWarningsSelector(invoiceId) {
  let cachedWarnings = EMPTY_ARRAY;
  let cachedSource = null;

  return createSelector(
    [selectWarningsList],
    (warnings) => {
      const source = warnings || EMPTY_ARRAY;
      if (source === cachedSource) return cachedWarnings;

      const next = source.filter(
        (item) => item.invoice && item.invoice.includes(invoiceId) && item.warntype !== 'warning'
      );
      if (isEqual(cachedWarnings, next)) return cachedWarnings;

      cachedSource = source;
      cachedWarnings = next;
      return next;
    }
  );
}

/** Per merchandise line — only validObjects_bl keys for this line and its duties */
export const selectMerchandiseLineValidation = (merchandiseId, merchandiseDutyIds = []) => {
  const dutyKey = (merchandiseDutyIds || []).slice().sort().join(',');
  const cacheKey = `${merchandiseId || ''}|${dutyKey}`;
  if (!merchLineValidationSelectorCache.has(cacheKey)) {
    merchLineValidationSelectorCache.set(
      cacheKey,
      buildMerchandiseLineValidationSelector(merchandiseId, merchandiseDutyIds)
    );
  }
  return merchLineValidationSelectorCache.get(cacheKey);
};

/** Per duty row — subset of line validation for one duty id */
export const selectMerchandiseDutyValidation = (merchandiseId, merchandiseDutyId) =>
  selectMerchandiseLineValidation(merchandiseId, merchandiseDutyId ? [merchandiseDutyId] : []);

/** Per invoice header — invoice-scoped validObjects_bl keys only */
export const selectInvoiceValidation = (invoiceId) => {
  const cacheKey = invoiceId || '';
  if (!invoiceValidationSelectorCache.has(cacheKey)) {
    invoiceValidationSelectorCache.set(cacheKey, buildInvoiceValidationSelector(invoiceId));
  }
  return invoiceValidationSelectorCache.get(cacheKey);
};

/** Warnings filtered to one invoice — stable ref when unchanged */
export const selectInvoiceWarnings = (invoiceId) => {
  const cacheKey = invoiceId || '';
  if (!invoiceWarningsSelectorCache.has(cacheKey)) {
    invoiceWarningsSelectorCache.set(cacheKey, buildInvoiceWarningsSelector(invoiceId));
  }
  return invoiceWarningsSelectorCache.get(cacheKey);
};
const selectFormValidationState = (state) => state.FormValidation;

// Memoized validation selectors
export const selectFieldValidation = createSelector(
  [selectFieldValidationState],
  (validation) => validation
);

export const selectCensusWarnings = createSelector(
  [selectFieldValidationState],
  (validation) => validation?.census_warnings
);

export const selectFormValidation = createSelector(
  [selectFormValidationState],
  (validation) => validation
);

export const selectServerWarnings = createSelector(
  [selectFieldValidationState],
  (validation) => validation?.serverwarnings_tm || []
);

export const selectValidationFields = createSelector(
  [selectFieldValidationState],
  (validation) => validation?.fields || []
);

// Additional convenience selectors
export const selectFieldValidations = createSelector(
  [selectFieldValidationState],
  (validation) => validation?.fields || []
);

export const selectWarnings = createSelector(
  [selectFieldValidationState],
  (validation) => validation?.warnings || []
);

// Add PSC selector
const selectPSCState = (state) => state.PSC;

export const selectPSCDataForMerchandise = createSelector(
  [selectPSCState],
  (psc) => psc
);

